import React from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { fmtINR, fmtFull } from "@/components/calculators/meta";

export const CalcField = ({ id, label, value, onChange, min, max, step = 1, prefix = "", suffix = "", money = false }) => (
  <div data-testid={`calc-field-${id}`}>
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={`in-${id}`} className="text-sm font-medium text-navy/80">{label}</label>
      <div className="flex items-center gap-1 rounded-xl border border-navy/10 bg-lightbg px-3 py-1.5">
        {prefix && <span className="text-sm text-ink/50">{prefix}</span>}
        <input
          id={`in-${id}`}
          data-testid={`calc-input-${id}`}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
          }}
          className="w-24 bg-transparent text-right font-display text-sm font-bold text-navy outline-none sm:w-28"
        />
        {suffix && <span className="text-sm text-ink/50">{suffix}</span>}
      </div>
    </div>
    <Slider
      data-testid={`calc-slider-${id}`}
      className="mt-3 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-2 [&_[role=slider]]:border-royal [&_[role=slider]]:bg-white [&_.bg-primary]:bg-royal [&_.bg-primary\/20]:bg-navy/10"
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={([v]) => onChange(v)}
    />
    <div className="mt-1.5 flex justify-between text-[11px] text-ink/40">
      <span>{money ? fmtINR(min) : `${min}${suffix}`}</span>
      <span>{money ? fmtINR(max) : `${max}${suffix}`}</span>
    </div>
  </div>
);

export const Stat = ({ id, label, value, sub, accent = false }) => (
  <div data-testid={`calc-stat-${id}`} className={`rounded-2xl p-5 ${accent ? "bg-navy text-white" : "border border-navy/[0.08] bg-white"}`}>
    <p className={`text-[11px] font-semibold uppercase tracking-wide ${accent ? "text-white/60" : "text-ink/45"}`}>{label}</p>
    <motion.p
      key={value}
      initial={{ opacity: 0.4, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`mt-2 font-display text-2xl font-extrabold tracking-tight sm:text-3xl ${accent ? "text-gold" : "text-navy"}`}
      title={typeof value === "number" ? fmtFull(value) : undefined}
    >
      {typeof value === "number" ? fmtINR(value) : value}
    </motion.p>
    {sub && <p className={`mt-1 text-xs ${accent ? "text-white/60" : "text-ink/50"}`}>{sub}</p>}
  </div>
);

// Two-part proportional bar (e.g. invested vs gains)
export const SplitBar = ({ a, b, labelA, labelB }) => {
  const total = a + b || 1;
  const pa = Math.max(2, Math.round((a / total) * 100));
  return (
    <div data-testid="calc-split-bar">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-navy/10">
        <motion.div className="h-full bg-navy" animate={{ width: `${pa}%` }} transition={{ duration: 0.5 }} />
        <motion.div className="h-full bg-gold" animate={{ width: `${100 - pa}%` }} transition={{ duration: 0.5 }} />
      </div>
      <div className="mt-2 flex justify-between text-xs text-ink/60">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-navy" /> {labelA} · {fmtINR(a)}</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-gold" /> {labelB} · {fmtINR(b)}</span>
      </div>
    </div>
  );
};

export const CalcShell = ({ inputs, results, note }) => (
  <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
    <div className="space-y-7 rounded-3xl border border-navy/[0.08] bg-white p-6 sm:p-8">{inputs}</div>
    <div className="space-y-4">
      {results}
      <p className="text-xs leading-relaxed text-ink/45">
        {note || "Illustrative only. Assumes a constant rate of return; actual returns vary and are not guaranteed. Not investment advice."}
      </p>
    </div>
  </div>
);
